import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { createArticle, updateArticle, autoDraftArticle } from '../../redux/knowledgeBaseSlice';
import { selectAppRoles } from '../../redux/userSlice';
import { Sparkles } from 'lucide-react';
import { toDisplayList, indentedLabel } from './categoryTree';
import VisibilityPicker from './VisibilityPicker';
import { labelStyle } from './kbStyles';

// Thunks reject with a normalised { message } object or, for older ones, a string.
const errorText = (err) => (typeof err === 'string' ? err : err?.message);

const ArticleEditorModal = ({ article, prefillTitle, categories, show, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const isNew = !article || article._prefillTitle;

  // Sub-categories listed under their parent, indented (WEB-830).
  const categoryOptions = useMemo(() => toDisplayList(categories), [categories]);

  const [form, setForm] = useState({
    title: article?.title || prefillTitle || '',
    subtitle: article?.subtitle || '',
    body: article?.body || '',
    categoryId: article?.category_id || (categories[0]?.category_id || ''),
    keywords: article?.keywords || '',
  });

  // Empty = everyone; enforced/interpreted server-side (WEB-734).
  const [selectedRoleIds, setSelectedRoleIds] = useState(article?.role_ids || []);
  const pickerValue = useMemo(() => ({ roleIds: selectedRoleIds }), [selectedRoleIds]);

  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [error, setError] = useState('');

  const appRoles = useSelector(selectAppRoles) || {};

  const isAdmin = Object.values(appRoles).some((role) => role.toLowerCase().includes('admin'));
  const isSRIUser = Object.values(appRoles).some((role) => role.toLowerCase().includes('sri'));
  const canManageRoles = isAdmin || isSRIUser;

  useEffect(() => {
    if (!show) return;
    setForm({
      title: article?.title || prefillTitle || '',
      subtitle: article?.subtitle || '',
      body: article?.body || '',
      categoryId: article?.category_id || categories[0]?.category_id || '',
      keywords: article?.keywords || '',
    });
    setSelectedRoleIds(article?.role_ids || []);
    setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article?.article_id, show]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleAutoDraft = async () => {
    if (!form.title.trim()) {
      setError('Enter a title first so the AI knows what to write.');
      return;
    }
    setDrafting(true);
    setError('');
    try {
      const categoryName = categories.find(c => c.category_id === parseInt(form.categoryId, 10))?.name || '';
      const result = await dispatch(autoDraftArticle({ topic: form.title, category: categoryName })).unwrap();
      setForm(f => ({
        ...f,
        subtitle: result.subtitle || f.subtitle,
        body: result.body || f.body,
        keywords: result.keywords || f.keywords,
      }));
    } catch (err) {
      // Surface the real reason (session expired, rate limited, API error)
      // instead of a generic line.
      setError(errorText(err) || 'Auto-draft failed. You can still write the article manually.');
    }
    setDrafting(false);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      setError('Title and body are required.');
      return;
    }
    const categoryIdNum = parseInt(form.categoryId, 10);
    if (isNaN(categoryIdNum)) {
      setError('Please select a category.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const data = {
        title: form.title,
        subtitle: form.subtitle || null,
        body: form.body,
        categoryId: categoryIdNum,
        keywords: form.keywords || null,
        roleIds: selectedRoleIds, // empty array = everyone, enforced/interpreted server-side
      };

      if (isNew) {
        await dispatch(createArticle(data)).unwrap();
      } else {
        await dispatch(updateArticle({ articleId: article.article_id, data })).unwrap();
      }
      onSaved();
    } catch (err) {
      setError(errorText(err) || 'Failed to save article.');
    }
    setSaving(false);
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '17px' }}>
          {isNew ? 'New Article' : 'Edit Article'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}

        <div className="d-flex gap-3 mb-3">
          <Form.Group className="flex-fill">
            <Form.Label className="text-muted text-uppercase" style={labelStyle}>
              Category *
            </Form.Label>
            <Form.Select value={form.categoryId} onChange={e => set('categoryId', e.target.value)}>
              {categoryOptions.map(c => (
                <option key={c.category_id} value={c.category_id}>{indentedLabel(c)}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>

        <Form.Group className="mb-3">
          <Form.Label className="text-muted text-uppercase" style={labelStyle}>
            Title *
          </Form.Label>
          <Form.Control value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. What is the redemption period?" />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="text-muted text-uppercase" style={labelStyle}>
            Subtitle / Summary
          </Form.Label>
          <Form.Control value={form.subtitle} onChange={e => set('subtitle', e.target.value)} placeholder="One-line description shown in search results" />
        </Form.Group>

        <Form.Group className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <Form.Label className="text-muted text-uppercase mb-0" style={labelStyle}>
              Article Body *
            </Form.Label>
            <Button
              size="sm"
              variant="outline-warning"
              onClick={handleAutoDraft}
              disabled={drafting}
              style={{ fontSize: '12px', fontWeight: 700 }}
            >
              {drafting ? (
                <>
                  <Spinner size="sm" animation="border" className="me-1" /> Drafting…
                </>
              ) : (
                <>
                  <Sparkles size={14} className="me-1" /> Auto-Draft with AI
                </>
              )}
            </Button>
          </div>
          <Form.Control
            as="textarea"
            rows={8}
            value={form.body}
            onChange={e => set('body', e.target.value)}
            placeholder="Write the article here."
            style={{ lineHeight: 1.65 }}
          />
        </Form.Group>

        <div className="d-flex gap-3 mb-3">
          <Form.Group className="flex-fill">
            <Form.Label className="text-muted text-uppercase" style={labelStyle}>
              Keywords
            </Form.Label>
            <Form.Control value={form.keywords} onChange={e => set('keywords', e.target.value)} placeholder="Redemption, Tax Lien (comma-separated)" />
          </Form.Group>
        </div>

        {canManageRoles && (
          <Form.Group className="mb-3">
            <VisibilityPicker
              mode="article"
              label="Who can view this article?"
              idPrefix="role-picker"
              value={pickerValue}
              onChange={(next) => setSelectedRoleIds(next.roleIds)}
            />
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          style={{ background: '#e85d04', border: 'none', fontWeight: 700 }}
        >
          {saving ? 'Saving…' : isNew ? 'Create Article' : 'Save Changes'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ArticleEditorModal;
